/**
 * KNGDM.IO client entry point.
 *
 * Wires four things together and does nothing else:
 *   transport  — where intents go and state comes from (in-process today, a
 *                WebSocket in Phase 10; this file is the only place that knows)
 *   renderer   — PixiJS layers, camera, hexes, borders
 *   input      — pointer and keyboard gestures turned into intents
 *   hud        — DOM overlay panels
 *
 * Deliberately thin. The moment game logic starts leaking into this file it has
 * leaked into the client, and the whole server-authoritative model is a fiction.
 * Everything here is plumbing, presentation, or camera state.
 */

import { Application } from "pixi.js";

import type {
  Biome,
  ClientIntent,
  HexCoord,
  HexState,
  KingdomId,
  KingdomState,
  MapState,
  ServerMessage,
} from "kngdm-shared";
import {
  checkClaim,
  hexKey,
  hexToPixel,
  neighbors,
} from "kngdm-shared";
import { TICK_RATE_HZ } from "kngdm-server/local";

import { InputController } from "./input/inputController";
import { createSinglePlayerGame } from "./net/localTransport";
import { Camera } from "./render/camera";
import { HexRenderer } from "./render/hexRenderer";
import { createLayers } from "./render/layers";
import { Hud } from "./ui/hud";
import { SetupScreen } from "./ui/setupScreen";

const HEX_SIZE = 20;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 3;

/** Zoom the game opens at: close enough to read individual hexes. */
const START_ZOOM = 1.6;

/**
 * Client-side mirror of the authoritative map, rebuilt from `fullState` and
 * patched by `stateDelta`.
 *
 * This is a *cache of what the server said*, never a second source of truth. It
 * is only ever written from inside the message handler.
 */
class WorldView {
  map: MapState | null = null;
  kingdoms: readonly KingdomState[] = [];
  ownKingdomId: KingdomId | null = null;
  tick = 0;
  sessionLengthTicks = 1;

  private readonly hexes = new Map<string, HexState>();

  setFullState(map: MapState, kingdoms: readonly KingdomState[], own: KingdomId): void {
    this.map = map;
    this.kingdoms = kingdoms;
    this.ownKingdomId = own;
    this.hexes.clear();
    for (const hex of map.hexes) this.hexes.set(hexKey(hex.coord), hex);
  }

  applyDelta(changed: readonly HexState[], kingdoms: readonly KingdomState[]): void {
    this.kingdoms = kingdoms;
    for (const hex of changed) this.hexes.set(hexKey(hex.coord), hex);
    if (this.map !== null) this.map = { ...this.map, hexes: [...this.hexes.values()] };
  }

  hexAt(coord: HexCoord): HexState | undefined {
    return this.hexes.get(hexKey(coord));
  }

  ownerAt(coord: HexCoord): KingdomId | null {
    return this.hexes.get(hexKey(coord))?.owner ?? null;
  }

  biomeAt(coord: HexCoord): Biome | null {
    return this.hexes.get(hexKey(coord))?.biome ?? null;
  }

  ownKingdom(): KingdomState | undefined {
    return this.kingdoms.find((k) => k.id === this.ownKingdomId);
  }

  ownedKeys(): ReadonlySet<string> {
    const keys = new Set<string>();
    if (this.ownKingdomId === null) return keys;
    for (const [key, hex] of this.hexes) {
      if (hex.owner === this.ownKingdomId) keys.add(key);
    }
    return keys;
  }

  territoryCount(): number {
    if (this.ownKingdomId === null) return 0;
    let count = 0;
    for (const hex of this.hexes.values()) if (hex.owner === this.ownKingdomId) count += 1;
    return count;
  }

  /**
   * Whether the player could claim this hex right now.
   *
   * Answered by `checkClaim` from the shared rules — the exact function the
   * server uses to authorise the same action. The client is not deciding
   * anything here; it is previewing the server's answer so the UI can grey out
   * what would be rejected. A second implementation of this logic would be a
   * desync by construction.
   */
  canClaim(coord: HexCoord): boolean {
    const kingdom = this.ownKingdom();
    const hex = this.hexAt(coord);
    if (kingdom === undefined || hex === undefined || this.ownKingdomId === null) return false;

    const neighbourOwners = new Map<string, KingdomId | null>();
    for (const n of neighbors(coord)) {
      neighbourOwners.set(hexKey(n), this.hexes.get(hexKey(n))?.owner ?? null);
    }

    return checkClaim(coord, {
      biome: hex.biome,
      owner: hex.owner,
      neighbourOwners,
      claimantId: this.ownKingdomId,
      claimantResources: kingdom.resources,
      claimantTerritoryCount: this.territoryCount(),
      claimantEliminated: kingdom.eliminated,
    }).allowed;
  }

  /** Every hex the player could claim this instant, for the render preview. */
  claimableCoords(): HexCoord[] {
    if (this.ownKingdomId === null) return [];
    const seen = new Set<string>();
    const result: HexCoord[] = [];

    for (const hex of this.hexes.values()) {
      if (hex.owner !== this.ownKingdomId) continue;
      for (const n of neighbors(hex.coord)) {
        const key = hexKey(n);
        if (seen.has(key)) continue;
        seen.add(key);
        if (this.canClaim(n)) result.push(n);
      }
    }
    return result;
  }
}

function mapPixelBounds(map: MapState, hexSize: number): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const hex of map.hexes) {
    const p = hexToPixel(hex.coord, hexSize);
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }

  const margin = hexSize * 2;
  return {
    minX: minX - margin,
    minY: minY - margin,
    maxX: maxX + margin,
    maxY: maxY + margin,
  };
}

/** Pixel centre of a kingdom's territory, or null if it holds nothing. */
function territoryCentroid(
  map: MapState,
  kingdomId: KingdomId,
  hexSize: number,
): { x: number; y: number } | null {
  let sumX = 0;
  let sumY = 0;
  let count = 0;

  for (const hex of map.hexes) {
    if (hex.owner !== kingdomId) continue;
    const p = hexToPixel(hex.coord, hexSize);
    sumX += p.x;
    sumY += p.y;
    count += 1;
  }

  return count === 0 ? null : { x: sumX / count, y: sumY / count };
}

/**
 * Where to point the camera when the session opens.
 *
 * The camera itself allows any hex on the map to be centred, which matters
 * because a spawn on an edge is otherwise unreachable. But *opening* centred on
 * an edge spawn fills half the screen with void, and an empty first frame reads
 * as a broken game rather than a big one.
 *
 * So the opening frame pulls back toward the map: centre on the kingdom where
 * there is room, and slide along each axis only as far as keeps the screen full.
 * The player can still centre on their capital afterwards if they want to.
 */
function openingFrame(
  target: { x: number; y: number },
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
  screen: { width: number; height: number },
  zoom: number,
): { x: number; y: number } {
  const halfWidth = screen.width / (2 * zoom);
  const halfHeight = screen.height / (2 * zoom);

  // Allow a strip of void rather than insisting the screen is completely full.
  // Refusing any void at all pushed an edge spawn ~600px off centre, which is
  // worse than a margin of empty space: the player could see their kingdom but
  // had to hunt for it.
  const fill = 0.6;

  const framed = (value: number, min: number, max: number, half: number): number => {
    const low = min + half * fill;
    const high = max - half * fill;
    // Viewport wider than the map on this axis: nothing to slide, sit centred.
    if (low > high) return (min + max) / 2;
    return Math.min(high, Math.max(low, value));
  };

  return {
    x: framed(target.x, bounds.minX, bounds.maxX, halfWidth),
    y: framed(target.y, bounds.minY, bounds.maxY, halfHeight),
  };
}

async function main(): Promise<void> {
  const canvas = document.getElementById("game");
  const uiRoot = document.getElementById("ui");
  if (!(canvas instanceof HTMLCanvasElement)) throw new Error("#game canvas missing");
  if (uiRoot === null) throw new Error("#ui overlay missing");

  const app = new Application();
  // Started, not awaited: WebGL initialisation and the player reading a form
  // have nothing to say to each other, and the form is by far the slower of the
  // two. By the time anyone clicks "Begin session" the renderer is ready.
  const appReady = app.init({
    canvas,
    resizeTo: window,
    antialias: true,
    background: 0x0c0d11,
    preference: "webgl",
  });

  // Session settings come from the player (or from the URL, which is how a seed
  // gets replayed and how the tools/ scripts skip the form). Nothing downstream
  // knows the difference — `createSession` validates all of it again anyway.
  const setup = new SetupScreen(uiRoot);
  const choice = await setup.choose();
  await appReady;

  const layers = createLayers(app);
  const world = new WorldView();

  const game = createSinglePlayerGame(choice);

  let camera: Camera | null = null;
  let renderer: HexRenderer | null = null;
  let input: InputController | null = null;
  let selected: HexCoord | null = null;
  let buildMode: Parameters<Hud["update"]>[0]["buildMode"] = null;
  let cullDirty = true;

  const hud = new Hud(uiRoot, {
    onIntent: (intent: ClientIntent) => game.transport.sendIntent(intent),
    onJumpToWorld: (point) => {
      camera?.centreOn(point);
      cullDirty = true;
    },
    onBuildModeChange: (building) => {
      buildMode = building;
      input?.setBuildMode(building);
    },
    onGodToolChange: (tool) => {
      input?.setGodTool(tool);
    },
  });

  /** Last pointer position, so the tooltip can follow without its own listener. */
  let pointerScreen = { x: 0, y: 0 };
  canvas.addEventListener("pointermove", (event) => {
    pointerScreen = { x: event.clientX, y: event.clientY };
  });

  /**
   * Drive the hover tooltip.
   *
   * Adjacency is computed here rather than inside the tooltip because it is a
   * question about the *player's* territory, not about the hex — and the world
   * view is the thing that knows which hexes are owned.
   */
  const showTooltipFor = (coord: HexCoord | null): void => {
    if (coord === null) {
      hud.tooltip.hide();
      return;
    }
    const hex = world.hexAt(coord);
    const kingdom = world.ownKingdom();
    if (hex === undefined || kingdom === undefined) {
      hud.tooltip.hide();
      return;
    }

    const owned = world.ownedKeys();
    hud.tooltip.show({
      hex,
      ownKingdomId: world.ownKingdomId,
      kingdoms: world.kingdoms,
      territoryCount: world.territoryCount(),
      resources: kingdom.resources,
      adjacent: neighbors(coord).some((n) => owned.has(hexKey(n))),
    });
    hud.tooltip.moveTo(pointerScreen.x, pointerScreen.y);
  };

  const bootstrap = (map: MapState, kingdoms: readonly KingdomState[], own: KingdomId): void => {
    world.setFullState(map, kingdoms, own);

    // A resync re-sends fullState. Without this, every resync would strand the
    // previous renderer's sprite pools and render textures on the GPU.
    renderer?.destroy();
    input?.destroy();

    const bounds = mapPixelBounds(map, HEX_SIZE);
    camera = new Camera(
      { width: app.screen.width, height: app.screen.height },
      { minZoom: MIN_ZOOM, maxZoom: MAX_ZOOM, bounds },
    );

    renderer = new HexRenderer(layers, app.renderer, { hexSize: HEX_SIZE });
    renderer.setKingdoms(kingdoms);
    renderer.setMap(map);

    // Open zoomed in on your own territory. `fitBounds` frames the whole 1600-hex
    // continent, which makes a seven-hex kingdom a smudge and makes the player's
    // first action fighting the camera.
    // Centroid of the whole starting territory, not the first hex in map order —
    // the difference is a hex or two, but it is the difference between the
    // kingdom sitting under the crosshair and sitting just beside it.
    const home = territoryCentroid(map, own, HEX_SIZE);
    if (home !== null) {
      const centre = home;
      camera.zoomAt({ x: app.screen.width / 2, y: app.screen.height / 2 }, START_ZOOM / camera.zoom);
      camera.centreOn(openingFrame(centre, bounds, app.screen, camera.zoom));
    }

    input = new InputController(
      canvas,
      camera,
      {
        hexSize: () => HEX_SIZE,
        ownedHexKeys: () => world.ownedKeys(),
        ownerAt: (coord) => world.ownerAt(coord),
        biomeAt: (coord) => world.biomeAt(coord),
        canClaimAt: (coord) => world.canClaim(coord),
      },
      {
        onIntent: (intent) => game.transport.sendIntent(intent),
        onHover: (coord) => {
          renderer?.setHover(coord);
          showTooltipFor(coord);
        },
        onSelect: (coord) => {
          selected = coord;
          renderer?.setSelected(coord);
        },
        onBuildModeChange: (building) => {
          buildMode = building;
          hud.setBuildMode?.(building);
        },
        onGodToolChange: (tool) => {
          hud.setGodTool?.(tool);
        },
        onCameraChange: () => {
          cullDirty = true;
        },
        onReject: (coord) => renderer?.flash(coord, "reject"),
      },
    );

    cullDirty = true;
  };

  game.transport.onMessage((message: ServerMessage) => {
    switch (message.type) {
      case "fullState":
        world.tick = message.tick;
        bootstrap(message.map, message.kingdoms, message.yourKingdomId);
        break;

      case "stateDelta":
        world.tick = message.tick;
        world.applyDelta(message.changedHexes, message.kingdoms);
        renderer?.setKingdoms(message.kingdoms);
        renderer?.applyDelta(message.changedHexes);
        break;

      case "sessionEnded":
        hud.showEnd(message, world.kingdoms, world.ownKingdomId ?? "");
        break;

      case "worldEvent":
        // Declared in the protocol, unimplemented until the world-events phase.
        break;
    }
  });

  world.sessionLengthTicks = game.session.state.sessionLengthTicks;

  window.addEventListener("resize", () => {
    camera?.resize({ width: app.screen.width, height: app.screen.height });
    cullDirty = true;
  });

  // Render at display rate, but only re-cull when the view actually moved, and
  // only refresh the HUD at the simulation's 10Hz. Rebuilding either every frame
  // is wasted work the render loop cannot afford.
  let lastHudTick = -1;
  app.ticker.add(() => {
    if (camera === null || renderer === null) return;

    // Edge panning is a rate, not an event: the cursor can sit still in the
    // margin and the camera still has to keep moving, so it is driven from the
    // frame loop rather than from a pointer handler.
    input?.update(app.ticker.deltaMS);

    // The renderer's own clock. Terrain and borders still redraw only when the
    // world changes; this advances the things the player should *feel* — capture
    // meters easing, contested rims pulsing, feedback flashes fading out.
    renderer.update(app.ticker.deltaMS);

    camera.applyTo(layers.world);
    if (cullDirty) {
      renderer.updateVisible(camera);
      cullDirty = false;
    }

    if (world.map !== null && world.ownKingdomId !== null && world.tick !== lastHudTick) {
      lastHudTick = world.tick;
      renderer.setClaimPreview(world.claimableCoords());
      hud.update({
        tick: world.tick,
        sessionLengthTicks: world.sessionLengthTicks,
        tickRateHz: TICK_RATE_HZ,
        map: world.map,
        kingdoms: world.kingdoms,
        ownKingdomId: world.ownKingdomId,
        hexSize: HEX_SIZE,
        cameraRect: camera.visibleWorldRect(),
        selected,
        buildMode,
      });
    }
  });

  // Dev-only handle for automated play-testing: it lets a headless browser click
  // a hex the player can actually claim, rather than guessing screen coordinates.
  // Stripped from production builds by Vite's dead-code elimination.
  if (import.meta.env.DEV) {
    (window as unknown as Record<string, unknown>).__kngdm = {
      session: game.session,
      world,
      camera: () => camera,
      hexSize: HEX_SIZE,
      /**
       * The live renderer, so a verification script can drive a cull and inspect
       * what got drawn rather than waiting on the frame loop.
       *
       * That distinction matters more than it sounds: rendering is driven by
       * `requestAnimationFrame`, which a browser suspends entirely when the page
       * is not compositing. In a headless or hidden window the simulation keeps
       * ticking (it runs on timers) while the HUD and the hex sprites sit frozen
       * at their initial state — which looks exactly like a rendering bug and is
       * not one. Calling `updateVisible` directly is how a script tells the two
       * apart.
       */
      renderer: () => renderer,
      /**
       * The input controller, so a script can drive `update()` by hand. Edge
       * panning is frame-driven, and `requestAnimationFrame` is suspended
       * whenever the page is not compositing — so in a hidden or headless window
       * the camera would never move and the feature would look broken when it is
       * merely unticked.
       */
      input: () => input,
      /** Screen positions of every hex the player could claim right now. */
      claimableScreenPoints: (): { x: number; y: number }[] =>
        camera === null
          ? []
          : world.claimableCoords().map((coord) => camera!.worldToScreen(hexToPixel(coord, HEX_SIZE))),
      ownedScreenPoints: (): { x: number; y: number }[] => {
        if (camera === null || world.map === null || world.ownKingdomId === null) return [];
        return world.map.hexes
          .filter((h) => h.owner === world.ownKingdomId)
          .map((h) => camera!.worldToScreen(hexToPixel(h.coord, HEX_SIZE)));
      },
    };
  }

  game.start();
}

main().catch((error: unknown) => {
  console.error("KNGDM.IO failed to start", error);
  const ui = document.getElementById("ui");
  if (ui !== null) {
    const panel = document.createElement("div");
    panel.className = "panel panel--boot";
    panel.textContent = `Failed to start: ${String(error)}`;
    ui.append(panel);
  }
});
